import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Calendar, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useRegistration } from '../../contexts/registration/hooks';
import { RegistrationStep } from './RegistrationStep';
import { RegistrationProgress } from './RegistrationProgress';
import { BusinessSteps } from './steps/business';
import LoadingScreen from '../LoadingScreen';
import BackWarningModal from './BackWarningModal';

type BusinessStep = {
  id: number;
  title: string;
  description: string;
};

export function RegistrationWizard() {
  const {
    state,
    loadingStates,
    goToPreviousStep,
    showBackWarningModal,
    setShowBackWarningModal,
    confirmGoBack
  } = useRegistration();
  const navigate = useNavigate();

  const currentStep = (BusinessSteps as BusinessStep[]).find(step => step.id === state.currentStep);
  const isInvalidStep = !currentStep;

  React.useEffect(() => {
    if (isInvalidStep) {

      navigate('/register?step=1', { replace: true });
    }
  }, [isInvalidStep, state.currentStep, navigate]);

  // חסימת כפתור החזור של הדפדפן בשלב 1
  React.useEffect(() => {
    if (state.currentStep === 1) {
      const handlePopState = (e: PopStateEvent) => {
        e.preventDefault();
        window.history.pushState(null, '', window.location.href);
      };

      // דחיפת state נוסף כך שלא נצא מהדף
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [state.currentStep]);

  if (isInvalidStep) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-50 bg-gradient-to-br from-indigo-50 via-white to-purple-50 border-b border-gray-200/50">
        {/* Logo and Back Button */}
        <div className="px-4 py-3">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              {state.currentStep > 1 && (
                <button
                  onClick={goToPreviousStep}
                  className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
                  disabled={loadingStates.updatingStep}
                >
                  <ArrowRight className="h-5 w-5" />
                  <span className="text-sm">חזור</span>
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-indigo-50 p-2 rounded-xl">
                <Calendar className="h-5 w-5 text-indigo-600" />
              </div>
              <span className="font-bold text-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-transparent bg-clip-text">
                LoyalFlow
              </span>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-4 pb-4">
          <div className="max-w-2xl mx-auto">
            <RegistrationProgress steps={BusinessSteps} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={state.currentStep === 5 ? '' : 'py-8 px-4'}>
        <div className={state.currentStep === 5 ? '' : 'max-w-2xl mx-auto'}>
          <AnimatePresence mode="wait">
            <motion.div
              key={state.currentStep}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <RegistrationStep
                step={currentStep}
                loading={loadingStates.updatingStep}
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <BackWarningModal
        isOpen={showBackWarningModal}
        onConfirm={confirmGoBack}
        onClose={() => setShowBackWarningModal(false)}
      />
    </div>
  );
}